import { createSelector } from 'reselect';
import { Flags, OneIdEnv, RenderTemplateTypes } from 'enums';
import { getEdition, getFaqUrl, getAdEdition } from 'config/i18n/editionMap';
import { queryString } from 'lib/format';
import bootstrapSelector from './bootstrapSelector';
import routingSelector from './routingSelector';
import whitelistSelector from './whitelistSelector';
import appConfig from '../../../config';
import appNames from '../../../config/appNames';

const appSelector = ({ app = {} }) => app;

export default createSelector(appSelector, (app) => app);

export const adobePassEnvSelector = createSelector(appSelector, ({ adobePassEnv }) => adobePassEnv);

export const apiEnvSelector = createSelector(appSelector, ({ apiEnv }) => apiEnv);

export const configEnvSelector = createSelector(appSelector, ({ configEnv }) => configEnv);

export const countrySelector = createSelector(appSelector, ({ country }) => country);

export const editionSelector = createSelector(countrySelector, (country) => getEdition(country));

export const isDomesticSelector = createSelector(countrySelector, (country) => country === 'US');

export const isIntlSelector = createSelector(isDomesticSelector, (isDomestic) => !isDomestic);

export const faqUrlSelector = createSelector(
    editionSelector,
    bootstrapSelector,
    ({ edition }, { faqUrls }) => getFaqUrl(edition, faqUrls));

export const adEditionSelector = createSelector(countrySelector, (country) => getAdEdition(country));

export const playbackMetaSelector = createSelector(appSelector, ({ playbackMeta }) => playbackMeta);

// Use format from config if available, otherwise choose based on domestic/intl
export const dateFormatSelector = createSelector(
    countrySelector,
    isIntlSelector,
    (country, isIntl) => (
        (getEdition(country).dateFormat) || (isIntl && 'dateTime3') || 'dateTime2'
    )
);

// For isIntl, format the date as `Weekday, number de month`; default to `dddd, MMMM Do`
export const upcomingScheduleDateFormatSelector = createSelector(
    isIntlSelector,
    (isIntl) => (
        (isIntl && 'date15') || 'date10'
    )
);

export const appNameSelector = createSelector(appSelector, ({ appName }) => appName);

export const isESPNAppSelector = createSelector(appSelector, ({ appName }) => appName === appNames.ESPNAPP);

export const webviewSelector = createSelector(
    appSelector,
    isESPNAppSelector,
    ({ webview }, isESPNApp) => (webview || isESPNApp)
);

export const hasPageShellSelector = createSelector(
    appSelector,
    ({ renderTemplateType }) => (renderTemplateType === RenderTemplateTypes.PAGE_SHELL)
);

// Enable Search if query param search=true OR if set to true in config AND is domestic edition
export const enableSearchSelector = createSelector(
    appSelector,
    isDomesticSelector,
    ({ appName, enableSearch }, isDomestic) => (
        enableSearch ||
        (appConfig.enableSearch && isDomestic && (appName !== appNames.ESPNAPP))
    )
);

export const oneIdEnvSelector = createSelector(
    appSelector,
    ({ location: { query = {} } }) => query[Flags.ONEID_ENV] || OneIdEnv.PROD
);

// FIXME: feedback page fails to populate intl providers on the server, fetching it client side
// Turn off SSR on Sched page to get localized date/times
export const ssrSelector = createSelector(
    appSelector,
    ({ appName, location: { query = {}, path = '' } = {} }) => (
        query[Flags.SSR] !== false &&
        (
            (appName !== appNames.ESPNAPP && path.indexOf('schedule') === -1) ||
            path.indexOf('choose-provider') !== -1
        )
    )
);

export const roadblockSelector = createSelector(
    appSelector,
    isIntlSelector,
    whitelistSelector,
    webviewSelector,
    ({ isMobile = false, options, location: { query = {} } = {} }, isIntl, inWhitelist, isWebview) => {
        // don't roadblock some lucky people
        if (inWhitelist || isWebview || isIntl) {
            return false;
        }

        if (query && query[Flags.ROADBLOCK] !== undefined) {
            return query[Flags.ROADBLOCK];
        }

        if (options && options[Flags.ROADBLOCK] !== undefined) {
            return options[Flags.ROADBLOCK];
        }

        return isMobile;
    }
);

export const unsupportedErrorSelector = createSelector(
    appSelector,
    ({ appName, location: { query = {}, path = '' } = {} }) => {
        if (
            query && query[Flags.UNSUPPORTED_ERROR] !== undefined &&
            appName !== appNames.ESPNAPP && path.indexOf('unsupported') === -1
        ) {
            return query[Flags.UNSUPPORTED_ERROR];
        }

        return false;
    }
);

export const playerTechSelector = createSelector(
    appSelector,
    isIntlSelector,
    whitelistSelector,
    ({ options, location: { query = {} } = {} }, isIntl, inWhitelist) => {
        // always let some lucky people get brightcove player
        if (inWhitelist || isIntl) {
            return 'brightcove';
        }

        // Supports assigning the playertech via query param `playertech=native || playertech=brightcove`
        if (query && query[Flags.PLAYER_TECH] !== undefined) {
            return query[Flags.PLAYER_TECH];
        }

        if (options && options[Flags.PLAYER_TECH] !== undefined) {
            return options[Flags.PLAYER_TECH];
        }

        return 'brightcove';
    }
);

export const intlSelector = createSelector(
    appSelector,
    ({ lang, messages }) => ({ lang, messages })
);

export const contentIdInPlayerUrl = createSelector(routingSelector, ({ locationBeforeTransitions }) => {
    let id = null;
    const { pathname = '', query = {} } = locationBeforeTransitions || {};
    if (pathname === '/' || pathname === '/player') {
        id = query.id || query.eventId;
        if (!id && pathname === '/player') {
            return true; // Return true to indicate on player page w/o ID
        }
    }
    return id;
});

export const hideNavSelector = createSelector(
    appSelector,
    ({ location: { query = {} } = {} }) => ((query && query[Flags.HIDENAV]) || false)
);

export const intlAppSelector = createSelector(
    hideNavSelector,
    isIntlSelector,
    (hideNav, isIntl) => hideNav && isIntl
);

export const bypassTokenSelector = createSelector(
    appSelector,
    ({ location: { query = {} } = {} }) => (query && query[Flags.BYPASS_TOKEN])
);

export const isSyndicatedPlayerSelector = createSelector(
    appSelector,
    ({ location: { path = '' } = {} }) => (path.indexOf('/syndicated/player') > -1)
);

export const packageFilterSelector = createSelector(
    isSyndicatedPlayerSelector,
    isSyndicatedPlayer => !isSyndicatedPlayer
);

export const isSecureSelector = createSelector(
    appSelector,
    ({ location: { protocol, path = '' } = {} }) => (
        protocol === 'https' ||
        path.indexOf('/activate') !== -1
    )
);

export const isAdDisabled = webviewSelector;

export const showDMVPDUpsellSelector = createSelector(
    isDomesticSelector,
    (isDomestic) => (
        isDomestic
    )
);

// returns the equivalent of `window.location` key/value pairs plus `query` which works universally
export const locationSelector = createSelector(
    appSelector,
    routingSelector,
    ({ location: { baseUrl, host, path, protocol: protocolArg, query = {} } = {} }, { locationBeforeTransitions: { hash } }) => {
        const tmp = host.split(':');
        const hostname = tmp[0];
        const port = tmp[1];
        const protocol = `${protocolArg}:`;
        const origin = `${protocol}//${host}`;
        const pathname = `${baseUrl}${path}`;
        const search = queryString(query, true);
        const href = `${origin}${pathname}${search}${hash}`;
        return {
            host,
            hostname,
            port,
            protocol,
            origin,
            pathname,
            search,
            href,
            hash,
            query
        };
    }
);

export const isLegacyAppSelector = createSelector(
    isESPNAppSelector,
    routingSelector,
    (isESPNApp, { locationBeforeTransitions: { query: { version, appversion } = {} } }) => {
        const ver = version || appversion; // `version` is for Android, `appversion` is for iOS
        return isESPNApp && ver && ver.indexOf('5') === 0;
    }
);

export const apiVersionSelector = createSelector(
    isLegacyAppSelector,
    (isLegacyApp) => (isLegacyApp ? 'v1' : 'v3')
);

export const upsellUrlSelector = createSelector(
    isESPNAppSelector,
    bootstrapSelector,
    editionSelector,
    () => 'https://promo.espn.com/espn/contests/instantaccess/'
);

export const newRelicTrackingSelector = createSelector(
    bootstrapSelector,
    ({ newrelic: { trackingOn } = {} }) => (trackingOn || false)
);

export const showOneIdLoginSelector = createSelector(
    appSelector,
    ({ location: { query: { login = false } = {} } = {} }) => login
);


export const showHelpModuleSelector = createSelector(
    bootstrapSelector,
    ({ activate: { helpModuleOn = true } = {} }) => helpModuleOn
);

export const playerOptionsSelector = createSelector(
    bootstrapSelector,
    ({ player: { options = {} } = {} }) => options
);




// WEBPACK FOOTER //
// ./app/common/selectors/appSelector.js